import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Eye, EyeOff } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { updateUser, type User } from '@/lib/traccar-api';
import { useResellerAccess } from '@/hooks/useResellerAccess';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { isSupportedLanguage } from '@/i18n';
import { UserLanguageSelect, DEFAULT_LANGUAGE } from './UserLanguageSelect';
import {
  fetchResellerAdminUsers, addResellerAdminUser, removeResellerAdminUser,
  type ResellerAdminUser,
} from '@/lib/reseller-api';

interface EditUserDialogProps {
  user: User;
  trigger?: React.ReactNode;
}

const userLanguage = (user: User) =>
  isSupportedLanguage(user.attributes?.language) ? user.attributes.language : DEFAULT_LANGUAGE;

export function EditUserDialog({ user, trigger }: EditUserDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isReseller } = useResellerAccess();
  const { user: sessionUser } = useTraccarSession();
  const viaReseller = sessionUser?.deviceLimit === -1 && sessionUser?.userLimit === -1;
  const [open, setOpen] = useState(false);
  const [confirmResellerAdmin, setConfirmResellerAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    password: '',
    isAdministrator: Boolean(user.attributes?.administrator),
    isEmployee: Boolean(user.attributes?.employee),
    isDeviceReadOnly: Boolean(user.deviceReadonly),
    isResellerAdmin: false,
    canCreateUsers: (user.userLimit ?? 0) !== 0,
    language: userLanguage(user),
  });

  const { data: resellerAdminUsers = [] } = useQuery({
    queryKey: ['reseller-admin-users'],
    queryFn: fetchResellerAdminUsers,
    enabled: open && isReseller,
    staleTime: 30000,
  });

  const resellerAdminEntry: ResellerAdminUser | undefined = resellerAdminUsers.find(
    (a) => a.traccar_user_id === user.id,
  );

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        isAdministrator: Boolean(user.attributes?.administrator),
        isEmployee: Boolean(user.attributes?.employee),
        isDeviceReadOnly: Boolean(user.deviceReadonly),
        isResellerAdmin: !!resellerAdminEntry,
        canCreateUsers: (user.userLimit ?? 0) !== 0,
        language: userLanguage(user),
      });
      setShowPassword(false);
    }
  };

  const updateMutation = useMutation({
    mutationFn: async (payload: User & { password?: string }) => {
      if (isReseller) {
        const wasAdmin = !!resellerAdminEntry;
        if (formData.isResellerAdmin && !wasAdmin) {
          await addResellerAdminUser({ traccar_user_id: user.id });
        } else if (!formData.isResellerAdmin && wasAdmin) {
          await removeResellerAdminUser(resellerAdminEntry!.id);
        }
      }
      return updateUser(payload, viaReseller);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['reseller-admin-users'] });
      toast.success(t('users.updateSuccess'));
      setOpen(false);
    },
    onError: (error: Error) => {
      const msg = error.message || t('users.updateError');
      toast.error(msg.length > 200 ? msg.slice(0, 200) + '…' : msg);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error(t('users.fillAllFields'));
      return;
    }

    const { language: _previousLanguage, ...attributes } = user.attributes ?? {};
    if (formData.language !== DEFAULT_LANGUAGE) attributes.language = formData.language;

    const payload: User & { password?: string } = {
      ...user,
      name: formData.name,
      email: formData.email,
      deviceReadonly: formData.isDeviceReadOnly,
      // Keep the existing limit unless the toggle changed, so a direct Traccar update
      // doesn't trip its limit-change check for non-admin managers
      userLimit: formData.canCreateUsers === ((user.userLimit ?? 0) !== 0)
        ? user.userLimit
        : formData.canCreateUsers ? 20 : 0,
      attributes: {
        ...attributes,
        administrator: formData.isAdministrator,
        employee: formData.isEmployee,
      },
    };

    if (formData.password) {
      payload.password = formData.password;
    }

    updateMutation.mutate(payload);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('users.editUser')}</DialogTitle>
            <DialogDescription>
              {t('users.editUserDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">{t('users.name')}</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('users.namePlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-email">{t('users.email')}</Label>
              <Input
                id="edit-email"
                type="text"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('users.emailPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-password">{t('users.password')}</Label>
              <div className="relative">
                <Input
                  id="edit-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('users.passwordPlaceholder')}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t('users.passwordOptional')}</p>
            </div>
            <UserLanguageSelect
              id="edit-language"
              value={formData.language}
              onChange={(language) => setFormData({ ...formData, language })}
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between gap-2 rounded-lg border p-3">
                <Label htmlFor="edit-device-readonly">{t('users.deviceReadonly')}</Label>
                <Switch
                  id="edit-device-readonly"
                  checked={formData.isDeviceReadOnly}
                  onCheckedChange={(checked) => setFormData({ ...formData, isDeviceReadOnly: checked })}
                />
              </div>
              <div className="flex items-center justify-between gap-2 rounded-lg border p-3">
                <Label htmlFor="edit-can-create-users">{t('users.canCreateUsers')}</Label>
                <Switch
                  id="edit-can-create-users"
                  checked={formData.canCreateUsers}
                  onCheckedChange={(checked) => setFormData({ ...formData, canCreateUsers: checked })}
                />
              </div>
              {isReseller && (
                <div className="flex items-center justify-between gap-2 rounded-lg border p-3">
                  <Label htmlFor="edit-reseller-admin">{t('users.resellerUser')}</Label>
                  <Switch
                    id="edit-reseller-admin"
                    checked={formData.isResellerAdmin}
                    onCheckedChange={(checked) => {
                      if (checked) setConfirmResellerAdmin(true);
                      else setFormData({ ...formData, isResellerAdmin: false });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={updateMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('common.update')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <AlertDialog open={confirmResellerAdmin} onOpenChange={setConfirmResellerAdmin}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('users.resellerAdmin')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('users.resellerAdminConfirm', { name: user.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={() => setFormData(f => ({ ...f, isResellerAdmin: true }))}>
            {t('common.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
